import { env } from '@/lib';

export interface LoginRequest {
  email: string;
  password: string;
}

export interface RegisterRequest {
  username: string;
  email: string;
  password: string;
}

export interface VerifyEmailRequest {
  email: string;
  token: string;
}

export interface ResetPasswordRequest {
  token: string;
  password: string;
}

export interface AcceptInviteRequest {
  username: string;
  password: string;
}

export interface RequestPasswordResetRequest {
  email: string;
}

export interface SetPasswordRequest {
  password: string;
}

export interface LoginResponse {
  success: boolean;
  message?: string;
}

export interface AuthError {
  status: number;
  message: string;
}

export function isAuthError(error: unknown): error is AuthError {
  return (
    typeof error === 'object' &&
    error !== null &&
    'status' in error &&
    'message' in error
  );
}

async function handleAuthResponse<T>(response: Response): Promise<T> {
  if (!response.ok) {
    let message = 'Authentication failed';
    try {
      const data = await response.json();
      message = data.message || data.error || message;
    } catch {
      message = response.statusText || message;
    }

    const error: AuthError = {
      status: response.status,
      message,
    };
    throw error;
  }

  if (response.status === 204) {
    return { success: true } as T;
  }

  return response.json();
}

export const authApi = {
  async login(credentials: LoginRequest): Promise<LoginResponse> {
    const response = await fetch(env.authBaseUrl + "/login", {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include',
      body: JSON.stringify(credentials),
    });

    return handleAuthResponse<LoginResponse>(response);
  },

  async logout(): Promise<void> {
    const response = await fetch(env.authBaseUrl + "/logout", {
      method: 'POST',
      credentials: 'include',
    });

    if (!response.ok) {
      const error: AuthError = {
        status: response.status,
        message: 'Logout failed',
      };
      throw error;
    }
  },

  async register(userData: RegisterRequest): Promise<LoginResponse> {
    const response = await fetch(env.authBaseUrl + "/register", {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include',
      body: JSON.stringify(userData),
    });

    return handleAuthResponse<LoginResponse>(response);
  },

  async verifyEmail(verifyData: VerifyEmailRequest): Promise<LoginResponse> {
    const response = await fetch(env.authBaseUrl + "/verifyEmail", {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include',
      body: JSON.stringify(verifyData),
    });

    return handleAuthResponse<LoginResponse>(response);
  },


  async acceptInvite(data: AcceptInviteRequest): Promise<LoginResponse> {
    const response = await fetch(env.authBaseUrl + "/acceptInvite", {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include',
      body: JSON.stringify(data),
    });

    return handleAuthResponse<LoginResponse>(response);
  },

  async requestPasswordReset(data: RequestPasswordResetRequest): Promise<LoginResponse> {
    const response = await fetch(env.authBaseUrl + "/requestPasswordReset", {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include',
      body: JSON.stringify(data),
    });

    return handleAuthResponse<LoginResponse>(response);
  },

  async setPassword(data: SetPasswordRequest): Promise<LoginResponse> {
    const response = await fetch(env.authBaseUrl + "/resetPassword", {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include',
      body: JSON.stringify(data),
    });

    return handleAuthResponse<LoginResponse>(response);
  },

  async changePassword(currentPassword: string, newPassword: string): Promise<LoginResponse> {
    const response = await fetch(env.authBaseUrl + "/change-password", {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include',
      body: JSON.stringify({
        currentPassword,
        newPassword,
      }),
    });

    return handleAuthResponse<LoginResponse>(response);
  },

  // Initiates the magic-link / OTP login flow by sending a code to the given email
  async startLogin(email: string): Promise<LoginResponse> {
    const response = await fetch(env.authBaseUrl + "/startLogin", {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include',
      body: JSON.stringify({ email }),
    });

    return handleAuthResponse<LoginResponse>(response);
  },

  // Verifies the OTP code received after startLogin and establishes a session
  async loginWithToken(verifyData: VerifyEmailRequest): Promise<LoginResponse> {
    const response = await fetch(env.authBaseUrl + "/loginWithToken", {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include',
      body: JSON.stringify(verifyData),
    });

    return handleAuthResponse<LoginResponse>(response);
  },

};
